import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { TextField } from '../../ui/text-field'
import { AsyncButton } from '../../ui/AsyncButton'
import { Section, Row } from '../../ui/section'
import { useAccount } from 'wagmi'
import { useIsAdmin } from '../../hooks/use-is-admin'
import { useIssueTagKeys, useActivateTag } from '../../api/provision-tag'

const AccessDenied = () => (
  <div className="min-h-screen bg-warm-50 flex items-center justify-center">
    <div className="rounded-2xl border border-warm-200 bg-white p-8 shadow-sm text-center max-w-sm">
      <h2 className="text-base font-semibold text-warm-900 mb-1">Access restricted</h2>
      <p className="text-sm text-warm-500">
        Only an on-chain admin wallet can provision NFC tags.
      </p>
    </div>
  </div>
)

const TagProvisionForm = () => {
  const { address } = useAccount()
  const issueKeys = useIssueTagKeys()
  const activateTag = useActivateTag()

  const form = useForm({
    defaultValues: {
      gtin:      '',
      batchUuid: '',
      uid:       '',
    },
    onSubmit: async ({ value }) => {
      if (!address) {
        alert('Please connect your wallet')
        return
      }
      await issueKeys.mutateAsync(value)
    },
  })

  const handleActivate = () => {
    const value = form.state.values
    return activateTag.mutateAsync(value)
  }

  return (
    <div className="min-h-screen bg-warm-50 px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold text-warm-900">Provision NFC Tag</h1>
          <p className="mt-1 text-sm text-warm-500">Issue keys for an existing batch, then activate the tag on-chain</p>
        </div>

        <div className="rounded-2xl border border-warm-200 bg-white p-6 shadow-sm">
          <form
            onSubmit={(e) => { e.preventDefault(); form.handleSubmit() }}
            className="flex flex-col gap-5"
          >
            <TextField form={form} name="gtin" label="GTIN" />
            <TextField form={form} name="batchUuid" label="Batch UUID" />
            <TextField form={form} name="uid" label="Tag UID (from OriginTag Scan)" />

            {issueKeys.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {issueKeys.error instanceof Error ? issueKeys.error.message : 'Failed to issue tag keys'}
              </div>
            )}

            <AsyncButton
              type="submit"
              variant="primary"
              pending={issueKeys.isPending || form.state.isSubmitting}
              className="mt-2 w-full"
            >
              {issueKeys.isPending ? 'Requesting keys…' : 'Get Keys'}
            </AsyncButton>
          </form>
        </div>

        {issueKeys.data && (
          <div className="mt-6 flex flex-col gap-4">
            <Section title="Write these into OriginTag">
              <Row label="Key1 (SDMMetaRead)" value={issueKeys.data.keys.key1} mono />
              <Row label="Key2 (SDMFileRead)" value={issueKeys.data.keys.key2} mono />
              <Row label="Link URL" value={issueKeys.data.url} mono />
            </Section>

            <div className="rounded-2xl border border-warm-200 bg-white p-6 shadow-sm">
              <p className="text-sm text-warm-500 mb-4">
                In OriginTag: write Key1/Key2 above via Change Key, then set the Link URL via Link. Once the tag reads back correctly, activate it below.
              </p>

              {activateTag.isError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {activateTag.error instanceof Error ? activateTag.error.message : 'Failed to activate tag'}
                </div>
              )}

              {activateTag.data ? (
                <Section title="Activated">
                  <Row label="Transaction" value={activateTag.data.txHash} mono />
                </Section>
              ) : (
                <AsyncButton
                  variant="secondary"
                  pending={activateTag.isPending}
                  onClick={handleActivate}
                  className="w-full"
                >
                  {activateTag.isPending ? 'Activating…' : 'Activate Tag'}
                </AsyncButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const TagProvision = () => {
  const isAdmin = useIsAdmin()
  return isAdmin ? <TagProvisionForm /> : <AccessDenied />
}

export const Route = createFileRoute('/_app/tag/provision')({
  component: TagProvision,
})
